import React from 'react';
import styled from 'styled-components';
import { Check, Clock, MapPin } from 'lucide-react';
import { formatDay, groupSessionsByDay, kindLabel } from 'helpers/eventSessions';
import colors from 'theme/colors';

const OK_TEXT = '#0a7a35';

/**
 * As sessões em que a pessoa marcou presença, junto do ingresso do evento:
 * é o que ela leva para o check-in, dia a dia e na ordem do horário.
 */
export default function WalletSessions({ sessions = [] }) {
    if (!sessions.length) {
        return null;
    }

    const days = groupSessionsByDay(sessions);

    return (
        <Section>
            <SectionHeader>
                <SectionTitle>Suas sessões</SectionTitle>
                <SectionHint>Presença marcada junto com o seu ingresso do evento</SectionHint>
            </SectionHeader>

            {days.map(day => {
                const { weekday, label } = formatDay(day.date);

                return (
                    <Day key={day.date}>
                        <DayTitle>{weekday}, {label}</DayTitle>

                        {day.items.map(session => (
                            <Item key={session.id}>
                                {session.image_url && <Cover src={session.image_url} alt='' loading='lazy' />}

                                <Info>
                                    <Kind>{kindLabel(session)}</Kind>
                                    <Title>{session.title}</Title>
                                    <Meta>
                                        <span><Clock size={13} /> {session.start}{session.end ? ` às ${session.end}` : ''}</span>
                                        {session.location && <span><MapPin size={13} /> {session.location}</span>}
                                    </Meta>
                                </Info>

                                <State $pending={session.status === 'reserved'}>
                                    {session.used_at
                                        ? <><Check size={14} strokeWidth={3} /> Presente</>
                                        : session.status === 'reserved'
                                            ? 'Aguardando o pagamento'
                                            : <><Check size={14} strokeWidth={3} /> Marcada</>}
                                </State>
                            </Item>
                        ))}
                    </Day>
                );
            })}
        </Section>
    );
}

const Section = styled.div`
    margin-bottom: 24px;
`;

const SectionHeader = styled.div`
    margin-bottom: 14px;
`;

const SectionTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 2px 0;
`;

const SectionHint = styled.p`
    font-size: 13px;
    color: ${colors.night};
    margin: 0;
`;

const Day = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 14px;
`;

const DayTitle = styled.h4`
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${colors.occasionalPurple};
`;

const Item = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 12px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.occasionalGrey};
`;

const Cover = styled.img`
    flex: none;
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 10px;
    background: ${colors.neutralDark};
`;

const Info = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

const Kind = styled.span`
    align-self: flex-start;
    padding: 1px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    background: ${colors.occasionalPurple}14;
`;

const Title = styled.b`
    font-size: 15px;
    line-height: 1.3;
    color: ${colors.nightDark};
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    font-size: 12px;
    color: ${colors.night};

    span {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }
`;

const State = styled.span`
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    color: ${({ $pending }) => ($pending ? colors.night : OK_TEXT)};
    background: ${({ $pending }) => ($pending ? colors.neutralDark : `${OK_TEXT}14`)};
`;
